import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { batchQuery, queryCollection, getUsers, User } from '../lib/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Pie, Bar, Line } from 'react-chartjs-2';
import { 
  FileText, 
  Download, 
  Calendar,
  CurrencyCircleDollar,
  WarningCircle,
  Wrench,
  Users,
  Buildings,
  ClipboardText,
  Spinner,
  Eye,
  CheckCircle,
  ChartPie,
  ChartBar
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export default function AnnualReportPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [organization, setOrganization] = useState<{ name?: string } | null>(null);
  const [reportData, setReportData] = useState<Record<string, unknown> | null>(null);
  
  // Chart refs for PDF export
  const expensePieRef = useRef(null);
  const issuesBarRef = useRef(null);
  const economyLineRef = useRef(null);
  
  // Available years
  const years = Array.from({ length: 5 }, (_, i) => 
    (new Date().getFullYear() - i).toString()
  );

  // Fetch organization and data
  useEffect(() => {
    if (user?.organization_id) {
      fetchOrganization();
      fetchReportData();
    }
  // fetchOrganization and fetchReportData depend on user?.organization_id and year which are in deps
  }, [user?.organization_id, year]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOrganization = async (): Promise<void> => {
    try {
      const result = await queryCollection('organizations', { id: user?.organization_id }, { limit: 1 });
      if (result?.[0]) {
        setOrganization(result[0] as { name?: string });
      } else {
        // Use default organization name if not found
        setOrganization({ name: 'Bostadsrättsförening' });
      }
    } catch (error) {
      console.error('Failed to fetch organization:', error);
      setOrganization({ name: 'Bostadsrättsförening' });
    }
  };

  const fetchReportData = async () => {
    if (!user?.organization_id) return;
    
    setLoading(true);
    try {
      const startDate = `${year}-01-01`;
      const endDate = `${year}-12-31`;
      
      const queries = [
        { collection: 'income_records', filter: { organization_id: user.organization_id }, limit: 2000 },
        { collection: 'expense_records', filter: { organization_id: user.organization_id }, limit: 2000 },
        { collection: 'issues', filter: { organization_id: user.organization_id }, limit: 2000 },
        { collection: 'maintenance_schedules', filter: { organization_id: user.organization_id }, limit: 500 },
        { collection: 'inspection_protocols', filter: { organization_id: user.organization_id }, limit: 500 },
        { collection: 'apartments', filter: { organization_id: user.organization_id }, limit: 500 },
        { collection: 'properties', filter: {}, limit: 100 },
        { collection: 'activity_log', filter: { organization_id: user.organization_id }, limit: 2000 }
      ];
      
      const results = await batchQuery(queries);
      
      // Get users from CommHub
      const commHubUsersResponse = await getUsers({ organization_id: user.organization_id || undefined });
      const users = commHubUsersResponse.data || [];
      
      // Filter data by year
      const filterByYear = (items: Record<string, unknown>[] | undefined, dateField: string) => 
        (items || []).filter(item => {
          const date = item[dateField];
          return typeof date === 'string' && date.startsWith(year);
        });
      
      const incomes = filterByYear(results.income_records as Record<string, unknown>[], 'date');
      const expenses = filterByYear(results.expense_records as Record<string, unknown>[], 'date');
      const issues = filterByYear(results.issues as Record<string, unknown>[], 'created_at');
      const maintenance = results.maintenance_schedules || [];
      const inspections = filterByYear(results.inspection_protocols as Record<string, unknown>[], 'inspection_date');
      const apartments = results.apartments || [];
      const activities = filterByYear(results.activity_log as Record<string, unknown>[], 'timestamp');
      
      // Calculate statistics
      const totalIncome = incomes.reduce((sum, i) => sum + (parseFloat(String(i.amount)) || 0), 0);
      const totalExpenses = expenses.reduce((sum, e) => sum + (parseFloat(String(e.amount)) || 0), 0);
      
      // Group expenses by category
      const expensesByCategory: Record<string, number> = {};
      expenses.forEach(e => {
        const cat = String(e.category || 'other');
        expensesByCategory[cat] = (expensesByCategory[cat] || 0) + (parseFloat(String(e.amount)) || 0);
      });
      
      // Issues statistics
      const issuesOpen = issues.filter(i => i.status === 'open').length;
      const issuesResolved = issues.filter(i => i.status === 'resolved' || i.status === 'closed').length;
      const issuesInProgress = issues.filter(i => i.status === 'in_progress').length;
      
      // Resident changes
      const moveIns = activities.filter(a => a.type === 'resident_move_in').length;
      const moveOuts = activities.filter(a => a.type === 'resident_move_out').length;
      
      // Monthly data for charts
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
      
      // Issues per month
      const issuesByMonth = Array(12).fill(0);
      issues.forEach(issue => {
        const createdAt = issue.created_at as string | undefined;
        if (createdAt) {
          const month = new Date(createdAt).getMonth();
          issuesByMonth[month]++;
        }
      });
      
      // Income and expenses per month
      const incomeByMonth = Array(12).fill(0);
      const expenseByMonth = Array(12).fill(0);
      
      incomes.forEach(income => {
        const incomeDate = income.date as string | undefined;
        const incomeAmount = income.amount;
        if (incomeDate) {
          const month = new Date(incomeDate).getMonth();
          incomeByMonth[month] += parseFloat(String(incomeAmount)) || 0;
        }
      });
      
      expenses.forEach(expense => {
        const expenseDate = expense.date as string | undefined;
        const expenseAmount = expense.amount;
        if (expenseDate) {
          const month = new Date(expenseDate).getMonth();
          expenseByMonth[month] += parseFloat(String(expenseAmount)) || 0;
        }
      });
      
      setReportData({
        incomes,
        expenses,
        issues,
        maintenance,
        inspections,
        users,
        apartments,
        activities,
        stats: {
          totalIncome,
          totalExpenses,
          balance: totalIncome - totalExpenses,
          expensesByCategory,
          issuesTotal: issues.length,
          issuesOpen,
          issuesResolved,
          issuesInProgress,
          inspectionsCount: inspections.length,
          maintenanceCount: maintenance.length,
          residentsCount: users.filter((u: { role?: string }) => u.role === 'resident').length,
          apartmentsCount: apartments.length,
          moveIns,
          moveOuts
        },
        chartData: {
          monthNames,
          issuesByMonth,
          incomeByMonth,
          expenseByMonth
        }
      } as Record<string, unknown>);
    } catch (error) {
      console.error('Failed to fetch report data:', error);
      toast.error('Kunde inte hämta rapportdata');
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Category labels and colors
  const categoryLabels = {
    heating: 'Värme',
    water: 'Vatten',
    electricity: 'El',
    garbage: 'Sopor',
    insurance: 'Försäkring',
    maintenance: 'Underhåll',
    other: 'Övrigt'
  };
  
  const categoryColors = {
    heating: '#f97316',
    water: '#3b82f6',
    electricity: '#eab308',
    garbage: '#22c55e',
    insurance: '#8b5cf6',
    maintenance: '#64748b',
    other: '#94a3b8'
  };

  // Chart configurations
  const displayStats = reportData?.stats as {
    expensesByCategory?: Record<string, number>;
    totalIncome?: number;
    totalExpenses?: number;
    balance?: number;
    issuesTotal?: number;
    issuesOpen?: number;
    issuesResolved?: number;
    issuesInProgress?: number;
    maintenanceCount?: number;
    inspectionsCount?: number;
    apartmentsCount?: number;
    residentsCount?: number;
    moveIns?: number;
    moveOuts?: number;
  } | undefined;
  const chartData = reportData?.chartData as { monthNames?: string[]; issuesByMonth?: number[]; incomeByMonth?: number[]; expenseByMonth?: number[] } | undefined;
  const categoryLabelsTyped = categoryLabels as Record<string, string>;
  const categoryColorsTyped = categoryColors as Record<string, string>;
  
  const expensePieData = displayStats?.expensesByCategory ? {
    labels: Object.keys(displayStats?.expensesByCategory ?? 0).map(k => categoryLabelsTyped[k] || k),
    datasets: [{
      data: Object.values(displayStats?.expensesByCategory ?? 0),
      backgroundColor: Object.keys(displayStats?.expensesByCategory ?? 0).map(k => categoryColorsTyped[k] || '#94a3b8'),
      borderWidth: 2,
      borderColor: '#ffffff'
    }]
  } : null;
  
  const issuesBarData = chartData ? {
    labels: chartData.monthNames,
    datasets: [{
      label: 'Ärenden',
      data: chartData.issuesByMonth,
      backgroundColor: '#f59e0b',
      borderRadius: 4
    }]
  } : null;
  
  const economyLineData = chartData ? {
    labels: chartData.monthNames,
    datasets: [
      {
        label: 'Intäkter',
        data: chartData.incomeByMonth,
        borderColor: '#22c55e',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        fill: true,
        tension: 0.3
      },
      {
        label: 'Utgifter',
        data: chartData.expenseByMonth,
        borderColor: '#ef4444',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        fill: true,
        tension: 0.3
      }
    ]
  } : null;
  
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          padding: 20,
          usePointStyle: true
        }
      }
    }
  };
  
  const barOptions = {
    ...chartOptions,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };
  
  const lineOptions = {
    ...chartOptions,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value: number | string) => formatCurrency(Number(value))
        }
      }
    }
  };

  // Generate PDF
  const generatePDF = async () => {
    if (!reportData || !organization) {
      toast.error('Data saknas för rapporten');
      return;
    }
    
    setGenerating(true);
    
    // Type-safe access to reportData properties
    const pdfStats = reportData.stats as {
      apartmentsCount: number;
      residentsCount: number;
      moveIns: number;
      moveOuts: number;
      totalIncome: number;
      totalExpenses: number;
      balance: number;
      issuesTotal: number;
      issuesOpen: number;
      issuesResolved: number;
      issuesInProgress: number;
      maintenanceCount: number;
      inspectionsCount: number;
      expensesByCategory: Record<string, number>;
    };
    const pdfMaintenance = (reportData.maintenance || []) as Array<{ title?: string; next_date?: string; interval?: string; category?: string; frequency?: string }>;
    const pdfInspections = (reportData.inspections || []) as Array<{ inspection_date?: string; building_name?: string; status?: string; type?: string; inspector?: string }>;
    const pdfIssues = (reportData.issues || []) as Array<{ title?: string; status?: string; priority?: string; created_at?: string }>;
    const pdfActivities = (reportData.activities || []) as Array<{ activity_type?: string; timestamp?: string; description?: string }>;
    const pdfApartments = (reportData.apartments || []) as Array<{ apartment_number?: string; area_sqm?: number; type?: string }>;
    const pdfUsers = (reportData.users || []) as User[];
    
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let yPos = 20;
      
      // Helper function for adding page breaks
      const checkPageBreak = (requiredSpace = 30) => {
        if (yPos + requiredSpace > pageHeight - 20) {
          doc.addPage();
          yPos = 20;
        }
      };
      
      // Helper function to add chart to PDF
      const addChartToPDF = (chartRef: React.RefObject<{ canvas?: HTMLCanvasElement } | null>, width = 180, height = 80) => {
        if (chartRef?.current) {
          const chartCanvas = chartRef.current.canvas;
          if (chartCanvas) {
            const imgData = chartCanvas.toDataURL('image/png');
            const xPos = (pageWidth - width) / 2;
            doc.addImage(imgData, 'PNG', xPos, yPos, width, height);
            yPos += height + 10;
          }
        }
      };
      
      // Title page
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.text('Årsrapport', pageWidth / 2, yPos, { align: 'center' });
      yPos += 15;
      
      doc.setFontSize(18);
      doc.text(organization.name || 'Bostadsrättsförening', pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'normal');
      doc.text(`Verksamhetsår ${year}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;
      
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Genererad: ${new Date().toLocaleDateString('sv-SE')}`, pageWidth / 2, yPos, { align: 'center' });
      doc.setTextColor(0);
      yPos += 30;
      
      // Section: Sammanfattning
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Sammanfattning', 14, yPos);
      yPos += 10;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      const summaryData = [
        ['Antal lägenheter', pdfStats.apartmentsCount.toString()],
        ['Antal boende', pdfStats.residentsCount.toString()],
        ['Inflyttningar under året', pdfStats.moveIns.toString()],
        ['Utflyttningar under året', pdfStats.moveOuts.toString()]
      ];
      
      autoTable(doc, {
        startY: yPos,
        head: [],
        body: summaryData,
        theme: 'plain',
        styles: { fontSize: 10 },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 60 },
          1: { cellWidth: 40 }
        }
      });
      
      yPos = doc.lastAutoTable.finalY + 15;
      
      // Section: Ekonomi
      checkPageBreak(60);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Ekonomisk översikt', 14, yPos);
      yPos += 10;
      
      const economyData = [
        ['Totala intäkter', formatCurrency(pdfStats.totalIncome)],
        ['Totala utgifter', formatCurrency(pdfStats.totalExpenses)],
        ['Resultat', formatCurrency(pdfStats.balance)]
      ];
      
      autoTable(doc, {
        startY: yPos,
        head: [['Post', 'Belopp']],
        body: economyData,
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246] },
        styles: { fontSize: 10 }
      });
      
      yPos = doc.lastAutoTable.finalY + 15;
      
      // Section: Utgifter per kategori
      checkPageBreak(60);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Utgifter per kategori', 14, yPos);
      yPos += 8;
      
      const categoryData = Object.entries(pdfStats.expensesByCategory)
        .map(([cat, amount]) => [categoryLabelsTyped[cat] || cat, formatCurrency(amount)])
        .sort((a, b) => parseFloat(b[1].replace(/[^\d,-]/g, '').replace(',', '.')) - parseFloat(a[1].replace(/[^\d,-]/g, '').replace(',', '.')));
      
      if (categoryData.length > 0) {
        autoTable(doc, {
          startY: yPos,
          head: [['Kategori', 'Belopp']],
          body: categoryData,
          theme: 'striped',
          headStyles: { fillColor: [239, 68, 68] },
          styles: { fontSize: 10 }
        });
        yPos = doc.lastAutoTable.finalY + 10;
        
        // Add pie chart
        checkPageBreak(90);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Utgiftsfördelning', 14, yPos);
        yPos += 5;
        addChartToPDF(expensePieRef, 100, 70);
      } else {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'italic');
        doc.text('Inga utgifter registrerade', 14, yPos);
        yPos += 10;
      }
      
      // Add economy line chart (income vs expenses over time)
      checkPageBreak(100);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Intäkter och utgifter per månad', 14, yPos);
      yPos += 5;
      addChartToPDF(economyLineRef, 180, 80);
      
      // Section: Ärenden
      checkPageBreak(50);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Felanmälningar och ärenden', 14, yPos);
      yPos += 10;
      
      const issuesData = [
        ['Totalt antal ärenden', pdfStats.issuesTotal.toString()],
        ['Öppna', pdfStats.issuesOpen.toString()],
        ['Pågående', pdfStats.issuesInProgress.toString()],
        ['Avslutade', pdfStats.issuesResolved.toString()]
      ];
      
      autoTable(doc, {
        startY: yPos,
        head: [['Status', 'Antal']],
        body: issuesData,
        theme: 'striped',
        headStyles: { fillColor: [245, 158, 11] },
        styles: { fontSize: 10 }
      });
      
      yPos = doc.lastAutoTable.finalY + 10;
      
      // Add issues bar chart
      checkPageBreak(90);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Ärenden per månad', 14, yPos);
      yPos += 5;
      addChartToPDF(issuesBarRef, 180, 70);
      
      // Section: Underhåll
      checkPageBreak(50);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Underhåll', 14, yPos);
      yPos += 10;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Antal aktiva underhållsscheman: ${pdfStats.maintenanceCount}`, 14, yPos);
      yPos += 6;
      doc.text(`Antal inspektioner under året: ${pdfStats.inspectionsCount}`, 14, yPos);
      yPos += 15;
      
      // List maintenance schedules
      if (pdfMaintenance.length > 0) {
        const maintenanceData = pdfMaintenance
          .slice(0, 10)
          .map(m => [
            m.title || 'Okänt',
            m.category || '-',
            m.frequency === 'yearly' ? 'Årligen' :
            m.frequency === 'monthly' ? 'Månadsvis' :
            m.frequency === 'quarterly' ? 'Kvartalsvis' : 'Engångs'
          ]);
        
        autoTable(doc, {
          startY: yPos,
          head: [['Åtgärd', 'Kategori', 'Intervall']],
          body: maintenanceData,
          theme: 'striped',
          headStyles: { fillColor: [34, 197, 94] },
          styles: { fontSize: 9 }
        });
        
        yPos = doc.lastAutoTable.finalY + 15;
      }
      
      // Section: Inspektioner
      if (pdfInspections.length > 0) {
        checkPageBreak(50);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Genomförda inspektioner', 14, yPos);
        yPos += 8;
        
        const inspectionData = pdfInspections
          .slice(0, 10)
          .map(i => [
            i.type || 'Inspektion',
            i.inspection_date ? new Date(i.inspection_date).toLocaleDateString('sv-SE') : '-',
            i.inspector || '-',
            i.status === 'completed' ? 'Slutförd' :
            i.status === 'draft' ? 'Utkast' : i.status || '-'
          ]);
        
        autoTable(doc, {
          startY: yPos,
          head: [['Typ', 'Datum', 'Inspektör', 'Status']],
          body: inspectionData,
          theme: 'striped',
          headStyles: { fillColor: [139, 92, 246] },
          styles: { fontSize: 9 }
        });
        
        yPos = doc.lastAutoTable.finalY + 15;
      }
      
      // Footer on each page
      const totalPages = (doc as any).internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
          `Sida ${i} av ${totalPages}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
        doc.text(
          `${organization.name || 'Förvaltningen'} - Årsrapport ${year}`,
          14,
          pageHeight - 10
        );
      }
      
      // Save the PDF
      const fileName = `arsrapport_${organization.name?.replace(/\s+/g, '_') || 'forening'}_${year}.pdf`;
      doc.save(fileName);
      
      toast.success('PDF-rapport genererad!');
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      toast.error('Kunde inte generera PDF');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8 space-y-6">
        <div className="h-8 w-64 bg-slate-200 rounded animate-pulse" />
        <div className="grid md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-slate-200 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="annual-report-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Årsmötesrapport</h1>
          <p className="text-slate-500">Generera PDF-rapport för årsmöte</p>
        </div>
        
        <div className="flex items-center gap-3">
          <div>
            <Label className="text-xs text-slate-500">Verksamhetsår</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(y => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Button 
            onClick={generatePDF}
            disabled={generating || !reportData}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {generating ? (
              <>
                <Spinner size={18} className="mr-2 animate-spin" />
                Genererar...
              </>
            ) : (
              <>
                <Download size={18} className="mr-2" />
                Ladda ner PDF
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Preview Cards */}
      {reportData && (
        <>
          {/* Organization info */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white mb-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                <Buildings size={32} />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{organization?.name || 'Bostadsrättsförening'}</h2>
                <p className="text-blue-100">Årsrapport {year}</p>
              </div>
            </div>
          </div>

          {/* Statistics */}
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CurrencyCircleDollar size={22} className="text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Intäkter</p>
                  <p className="text-xl font-bold text-slate-900">
                    {formatCurrency(displayStats?.totalIncome ?? 0)}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <CurrencyCircleDollar size={22} className="text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Utgifter</p>
                  <p className="text-xl font-bold text-slate-900">
                    {formatCurrency(displayStats?.totalExpenses ?? 0)}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <WarningCircle size={22} className="text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Ärenden</p>
                  <p className="text-xl font-bold text-slate-900">
                    {displayStats?.issuesTotal ?? 0}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <ClipboardText size={22} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Inspektioner</p>
                  <p className="text-xl font-bold text-slate-900">
                    {displayStats?.inspectionsCount ?? 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Report Preview */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Eye size={18} />
                Förhandsgranskning
              </h3>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Sammanfattning */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <Buildings size={18} className="text-blue-600" />
                  Sammanfattning
                </h4>
                <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-500">Lägenheter</p>
                    <p className="text-2xl font-bold text-slate-900">{displayStats?.apartmentsCount ?? 0}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-500">Boende</p>
                    <p className="text-2xl font-bold text-slate-900">{displayStats?.residentsCount ?? 0}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-500">Inflyttningar</p>
                    <p className="text-2xl font-bold text-green-600">+{displayStats?.moveIns ?? 0}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-500">Utflyttningar</p>
                    <p className="text-2xl font-bold text-red-600">-{displayStats?.moveOuts ?? 0}</p>
                  </div>
                </div>
              </div>

              {/* Ekonomi */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <CurrencyCircleDollar size={18} className="text-green-600" />
                  Ekonomi
                </h4>
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Totala intäkter</p>
                      <p className="text-xl font-bold text-green-600">{formatCurrency(displayStats?.totalIncome ?? 0)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Totala utgifter</p>
                      <p className="text-xl font-bold text-red-600">{formatCurrency(displayStats?.totalExpenses ?? 0)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Resultat</p>
                      <p className={`text-xl font-bold ${displayStats?.balance ?? 0 >= 0 ? 'text-blue-600' : 'text-amber-600'}`}>
                        {formatCurrency(displayStats?.balance ?? 0)}
                      </p>
                    </div>
                  </div>
                </div>
                
                {Object.keys(displayStats?.expensesByCategory ?? 0).length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm text-slate-500 mb-2">Utgifter per kategori</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(displayStats?.expensesByCategory ?? {})
                        .sort(([, a], [, b]) => (b as number) - (a as number))
                        .map(([cat, amount]) => (
                          <div 
                            key={cat}
                            className="px-3 py-2 bg-slate-100 rounded-lg text-sm"
                          >
                            <span className="font-medium">{categoryLabelsTyped[cat] || cat}:</span>
                            <span className="ml-1 text-slate-600">{formatCurrency(amount as number)}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Charts Section */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <ChartBar size={18} className="text-purple-600" />
                  Diagram
                </h4>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Expense Pie Chart */}
                  {expensePieData && Object.keys(displayStats?.expensesByCategory ?? 0).length > 0 && (
                    <div className="bg-slate-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                        <ChartPie size={16} />
                        Utgiftsfördelning
                      </p>
                      <div className="h-64">
                        <Pie ref={expensePieRef} data={expensePieData} options={chartOptions} />
                      </div>
                    </div>
                  )}
                  
                  {/* Issues Bar Chart */}
                  {issuesBarData && (
                    <div className="bg-slate-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                        <ChartBar size={16} />
                        Ärenden per månad
                      </p>
                      <div className="h-64">
                        <Bar ref={issuesBarRef} data={issuesBarData} options={barOptions} />
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Economy Line Chart */}
                {economyLineData && (
                  <div className="bg-slate-50 rounded-lg p-4 mt-4">
                    <p className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                      <CurrencyCircleDollar size={16} />
                      Intäkter och utgifter per månad
                    </p>
                    <div className="h-64">
                      <Line ref={economyLineRef} data={economyLineData} options={lineOptions} />
                    </div>
                  </div>
                )}
              </div>

              {/* Ärenden */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <WarningCircle size={18} className="text-amber-600" />
                  Ärenden
                </h4>
                <div className="grid sm:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-blue-600">{displayStats?.issuesTotal ?? 0}</p>
                    <p className="text-sm text-blue-700">Totalt</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-amber-600">{displayStats?.issuesOpen ?? 0}</p>
                    <p className="text-sm text-amber-700">Öppna</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-purple-600">{displayStats?.issuesInProgress ?? 0}</p>
                    <p className="text-sm text-purple-700">Pågående</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-green-600">{displayStats?.issuesResolved ?? 0}</p>
                    <p className="text-sm text-green-700">Avslutade</p>
                  </div>
                </div>
              </div>

              {/* Underhåll */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <Wrench size={18} className="text-slate-600" />
                  Underhåll
                </h4>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-500">Aktiva scheman</p>
                    <p className="text-2xl font-bold text-slate-900">{displayStats?.maintenanceCount ?? 0}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-500">Inspektioner under året</p>
                    <p className="text-2xl font-bold text-slate-900">{displayStats?.inspectionsCount ?? 0}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Download reminder */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText size={20} className="text-blue-600" />
              </div>
              <div>
                <h4 className="font-medium text-blue-900">Redo att ladda ner</h4>
                <p className="text-sm text-blue-700 mt-1">
                  Klicka på "Ladda ner PDF" för att generera en professionell rapport som kan 
                  delas med medlemmarna inför årsmötet.
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
